/**
 * Assignment Status Constants
 *
 * Centralized definitions for all cast assignment statuses
 * to ensure consistency across the application
 *
 * Simplified workflow (January 2025):
 * - Admin creates: 'offered' (direct assignment)
 * - Dancer responds: 'accepted' or 'declined'
 * - For standby dancers: use B Cast instead of separate status
 */

/**
 * All possible assignment statuses
 */
export const ASSIGNMENT_STATUSES = {
  // Pending status (requires dancer response)
  OFFERED: 'offered', // Direct offer (also covers legacy 'pending', 'assigned', 'invited')

  // Response statuses
  ACCEPTED: 'accepted', // Dancer accepted (final positive state)
  DECLINED: 'declined', // Dancer declined
} as const;

/**
 * Assignment status groups for categorization
 */
export const ASSIGNMENT_STATUS_GROUPS = {
  // Statuses requiring dancer response
  PENDING: [ASSIGNMENT_STATUSES.OFFERED] as const,

  // Accepted statuses
  ACCEPTED: [ASSIGNMENT_STATUSES.ACCEPTED] as const,

  // Declined statuses
  DECLINED: [ASSIGNMENT_STATUSES.DECLINED] as const,
} as const;

/**
 * Type for assignment status values
 */
export type AssignmentStatus =
  (typeof ASSIGNMENT_STATUSES)[keyof typeof ASSIGNMENT_STATUSES];

/**
 * Type for assignment status group keys
 */
export type AssignmentStatusGroup = keyof typeof ASSIGNMENT_STATUS_GROUPS;
